import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { FOOTER_APP_STORE_LINKS } from '../../../config/footerContact';
import './HeroSection.css';
import { DEFAULT_HERO_CAROUSEL_BG, heroPromoBackgroundStyle } from './heroCarouselBackground';

/** Official marks (Wikimedia Commons). */
const APP_STORE_ICON =
  'https://upload.wikimedia.org/wikipedia/commons/6/67/App_Store_%28iOS%29.svg';
const GOOGLE_PLAY_ICON =
  'https://upload.wikimedia.org/wikipedia/commons/5/55/Google_Play_2016_icon.svg';
const APP_STORE_ICON_FALLBACK = '/store-badges/app-store-icon.svg';
const GOOGLE_PLAY_ICON_FALLBACK = '/store-badges/google-play-icon.svg';

export type HeroSlideLayout = 'default' | 'app_stores';

export type HeroSlide = {
  id: string;
  kicker: string;
  line1: string;
  line2: string;
  slide_layout?: HeroSlideLayout;
  background_color?: string;
};

export type HeroSlideInput = {
  slug: string;
  kicker: string;
  line1: string;
  line2: string;
  slide_layout?: HeroSlideLayout;
  background_color?: string;
};

const FALLBACK_HERO_SLIDES: HeroSlide[] = [
  {
    id: 'brand',
    kicker: 'Your door to new opportunities',
    line1: 'CANADAWALE YAR',
    line2: '',
    slide_layout: 'default',
    background_color: DEFAULT_HERO_CAROUSEL_BG,
  },
  {
    id: 'opportunities',
    kicker: 'Your door to new opportunities',
    line1: 'Expert help for',
    line2: 'life in Canada',
  },
  {
    id: 'app-stores',
    kicker: 'Your door to new opportunities',
    line1: 'Available on',
    line2: 'App Store & Play Store',
    slide_layout: 'app_stores',
  },
  {
    id: 'rcic',
    kicker: 'Your door to new opportunities',
    line1: 'RCIC verified',
    line2: 'immigration help',
  },
  {
    id: 'booking',
    kicker: 'Your door to new opportunities',
    line1: 'Book consultants',
    line2: 'in minutes',
  },
  {
    id: 'store',
    kicker: 'Your door to new opportunities',
    line1: 'CWY Store',
    line2: 'courses & services',
  },
  {
    id: 'chat',
    kicker: 'Your door to new opportunities',
    line1: 'Chat with trusted',
    line2: 'professionals',
  },
  {
    id: 'services',
    kicker: 'Your door to new opportunities',
    line1: 'Legal, Tax,',
    line2: 'Automotive & more',
  },
  {
    id: 'wallet',
    kicker: 'Your door to new opportunities',
    line1: 'Send, receive,',
    line2: 'manage CWY tokens',
  },
];

function mapCarouselInput(input: HeroSlideInput[]): HeroSlide[] {
  return input.map((slide) => ({
    id: slide.slug,
    kicker: slide.kicker,
    line1: slide.line1,
    line2: slide.line2,
    slide_layout: slide.slide_layout ?? (slide.slug === 'app-stores' ? 'app_stores' : 'default'),
    background_color: slide.background_color,
  }));
}

function isAppStoresSlide(slide: HeroSlide): boolean {
  return slide.slide_layout === 'app_stores' || slide.id === 'app-stores';
}

type HeroSectionProps = {
  slides?: HeroSlideInput[];
};

const SLIDE_MS = 5500;
const TRANSITION_EASE = [0.32, 0.72, 0, 1] as const;

const slideVariants = {
  enter: (dir: number) => ({
    opacity: 0,
    x: dir > 0 ? 80 : -80,
  }),
  center: {
    opacity: 1,
    x: 0,
  },
  exit: (dir: number) => ({
    opacity: 0,
    x: dir > 0 ? -80 : 80,
  }),
};

const textStagger = {
  enter: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } },
  center: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } },
  exit: { transition: { staggerChildren: 0.04, staggerDirection: -1 } },
};

const textLineVariants = {
  enter: {
    opacity: 0,
    x: 36,
    filter: 'blur(8px)',
  },
  center: {
    opacity: 1,
    x: 0,
    filter: 'blur(0px)',
  },
  exit: {
    opacity: 0,
    x: -28,
    filter: 'blur(6px)',
  },
};

const reducedSlideVariants = {
  enter: { opacity: 0 },
  center: { opacity: 1 },
  exit: { opacity: 0 },
};

const storeHeadlineStagger = {
  enter: { transition: { staggerChildren: 0.08, delayChildren: 0.04 } },
  center: { transition: { staggerChildren: 0.08, delayChildren: 0.04 } },
};

/** Icons burst outward from the headline after copy is visible. */
const STORE_ICON_FROM_TEXT_DELAY = 0.34;

const storeIconFromTextTransition = {
  duration: 0.62,
  ease: TRANSITION_EASE,
  delay: STORE_ICON_FROM_TEXT_DELAY,
};

const storeIconLeftVariants = {
  enter: {
    opacity: 0,
    scale: 0.12,
    rotate: 0,
    x: 72,
  },
  center: {
    opacity: 1,
    scale: 1,
    rotate: -12,
    x: 0,
    transition: storeIconFromTextTransition,
  },
  exit: {
    opacity: 0,
    scale: 0.2,
    rotate: 0,
    x: 48,
    transition: { duration: 0.32, ease: TRANSITION_EASE },
  },
};

const storeIconRightVariants = {
  enter: {
    opacity: 0,
    scale: 0.12,
    rotate: 0,
    x: -72,
  },
  center: {
    opacity: 1,
    scale: 1,
    rotate: 12,
    x: 0,
    transition: storeIconFromTextTransition,
  },
  exit: {
    opacity: 0,
    scale: 0.2,
    rotate: 0,
    x: -48,
    transition: { duration: 0.32, ease: TRANSITION_EASE },
  },
};

export const HeroSection: React.FC<HeroSectionProps> = ({ slides: slidesProp }) => {
  const reduceMotion = useReducedMotion();

  const slides = useMemo(
    () => (slidesProp && slidesProp.length > 0 ? mapCarouselInput(slidesProp) : FALLBACK_HERO_SLIDES),
    [slidesProp]
  );
  const slideCount = slides.length;

  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);

  useEffect(() => {
    setCurrent((c) => (slideCount === 0 ? 0 : c >= slideCount ? 0 : c));
  }, [slideCount]);

  const goTo = useCallback(
    (index: number) => {
      if (slideCount === 0) return;
      const next = (index + slideCount) % slideCount;
      setCurrent((prev) => {
        setDirection(next > prev ? 1 : next < prev ? -1 : 1);
        return next;
      });
    },
    [slideCount]
  );

  useEffect(() => {
    if (slideCount === 0) return undefined;
    const timer = window.setInterval(() => {
      setDirection(1);
      setCurrent((c) => (c + 1) % slideCount);
    }, SLIDE_MS);
    return () => window.clearInterval(timer);
  }, [slideCount]);

  if (slideCount === 0) return null;

  const slide = slides[current];
  const variants = reduceMotion ? reducedSlideVariants : slideVariants;
  const lineVariants = reduceMotion ? reducedSlideVariants : textLineVariants;

  return (
    <section
      className="hero-promo"
      aria-label="Promotions"
      style={heroPromoBackgroundStyle(slide.background_color)}
    >
      <div className="hero-promo__rings" aria-hidden />
      <div className="hero-promo__swoosh" aria-hidden />

      <div className="hero-promo__carousel">
        <div className="hero-promo__slides-inner">
          <div className="hero-promo__anim-wrap">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={slide.id}
                className="hero-promo__slide"
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  duration: reduceMotion ? 0.2 : 0.55,
                  ease: TRANSITION_EASE,
                }}
              >
                <motion.div
                  className="hero-promo__slide-text"
                  variants={reduceMotion ? undefined : textStagger}
                  initial="enter"
                  animate="center"
                >
                  <motion.p className="hero-promo__kicker" variants={lineVariants}>
                    {slide.kicker}
                  </motion.p>
                  {isAppStoresSlide(slide) ? (
                    <motion.div
                      className="hero-promo__store-row"
                      initial="enter"
                      animate="center"
                      aria-label="Available on Google Play and App Store"
                    >
                      <motion.h1
                        className="hero-promo__headline hero-promo__headline--store"
                        variants={reduceMotion ? undefined : storeHeadlineStagger}
                        initial="enter"
                        animate="center"
                      >
                        {slide.line1 ? (
                          <motion.span className="hero-promo__headline-line" variants={lineVariants}>
                            {slide.line1}
                          </motion.span>
                        ) : null}
                        {slide.line2 ? (
                          <motion.span className="hero-promo__headline-accent" variants={lineVariants}>
                            {slide.line2}
                          </motion.span>
                        ) : null}
                      </motion.h1>
                      <motion.a
                        href={FOOTER_APP_STORE_LINKS.android}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hero-promo__store-icon-link hero-promo__store-icon-link--side hero-promo__store-icon-link--play"
                        aria-label="Google Play"
                        variants={reduceMotion ? reducedSlideVariants : storeIconLeftVariants}
                        initial="enter"
                        animate="center"
                        whileHover={reduceMotion ? undefined : { scale: 1.06 }}
                      >
                        <img
                          src={GOOGLE_PLAY_ICON}
                          alt=""
                          className="hero-promo__store-icon-img"
                          width={80}
                          height={80}
                          decoding="async"
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            const img = e.currentTarget;
                            if (!img.src.includes(GOOGLE_PLAY_ICON_FALLBACK)) {
                              img.src = GOOGLE_PLAY_ICON_FALLBACK;
                            }
                          }}
                        />
                      </motion.a>
                      <motion.a
                        href={FOOTER_APP_STORE_LINKS.ios}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hero-promo__store-icon-link hero-promo__store-icon-link--side hero-promo__store-icon-link--app"
                        aria-label="App Store"
                        variants={reduceMotion ? reducedSlideVariants : storeIconRightVariants}
                        initial="enter"
                        animate="center"
                        whileHover={reduceMotion ? undefined : { scale: 1.06 }}
                      >
                        <img
                          src={APP_STORE_ICON}
                          alt=""
                          className="hero-promo__store-icon-img"
                          width={80}
                          height={80}
                          decoding="async"
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            const img = e.currentTarget;
                            if (!img.src.includes(APP_STORE_ICON_FALLBACK)) {
                              img.src = APP_STORE_ICON_FALLBACK;
                            }
                          }}
                        />
                      </motion.a>
                    </motion.div>
                  ) : (
                    <h1
                      className={`hero-promo__headline${slide.id === 'brand' ? ' hero-promo__headline--brand' : ''}`}
                    >
                      {slide.line1 ? (
                        <motion.span
                          className={`hero-promo__headline-line${slide.id === 'brand' ? ' hero-promo__headline-line--brand' : ''}`}
                          variants={lineVariants}
                        >
                          {slide.line1}
                        </motion.span>
                      ) : null}
                      {slide.line2 ? (
                        <motion.span className="hero-promo__headline-accent" variants={lineVariants}>
                          {slide.line2}
                        </motion.span>
                      ) : null}
                    </h1>
                  )}
                </motion.div>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="hero-promo__dots" role="tablist" aria-label="Hero slides">
            {slides.map((s, index) => (
              <button
                key={s.id}
                type="button"
                role="tab"
                aria-selected={index === current}
                aria-label={`Slide ${index + 1}: ${[s.line1, s.line2].filter(Boolean).join(' ') || s.kicker}`}
                className={`hero-promo__dot${index === current ? ' hero-promo__dot--active' : ''}`}
                onClick={() => goTo(index)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
